import axios from 'axios';

import queryString from 'query-string';
import { of, BehaviorSubject, from } from 'rxjs';
import { map, filter, debounceTime, distinctUntilChanged, catchError, switchMap, first } from 'rxjs/operators';
import {
   getCompanyAutosuggestJF,
   getHiringCompanyAutosuggestJF,
   getJobGroupAutosuggestJF,
   getOrganizationAutosuggestJF, getPublisherAutosuggestJF,
} from '../../../services/partner/autosuggest';
import { getContractSASuggestions } from '../../../sponsoredAds/services/autosuggestServices';
import { isKeepTwinPath } from '../../../../util/keepTwin';

const BASE_URL = `${ process.env.REACT_APP_AOE_SECURE_WITH_PATH }/api/`;
const AUTO_SUGGEST_BASE_URL = `${ process.env.REACT_APP_AOE_SECURE_WITH_PATH }/api/autoSuggest/`;
const CHANGE_LOG_BASE_URL = `${ process.env.REACT_APP_AOE_SECURE_WITH_PATH }/api/log/`;
const AUTO_SUGGEST_DEBOUNCE_TIME = 250;

const getDomain = () => {
   if (
      window?.location?.origin
      && (
         window.location.origin === 'https://www.upward.net'
         || window.location.origin === 'https://upward.net'
      )
   ) {
      return 'https://services.upward.net/v1/sjm';
   } else {
      return 'https://stageservices.upward.net/v1/sjm';
   }
};

export const getLocation = (keyword = '', params) => {
   return from(
      axios.get(`${ process.env.REACT_APP_AOE_SECURE_WITH_PATH }/api/feedManagement/autosuggest/location?keyword=${ keyword }${ params ? `&${ queryString.stringify(params) }` : '' }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            const locations = response.data;
            const formatted_locations = locations.map((location) => {
               return {
                  text: location.name,
                  latitude: location.latitude,
                  longitude: location.longitude,
                  id: location.id,
                  locationFullName: location.fullName,
               };
            });
            return formatted_locations;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const locationAutoCompleteInput$ = new BehaviorSubject('');
export const locationAutoCompleteObservable$ = locationAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         return getLocation(strInput).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getBasicLocation = (keyword = '', params) => {
   return from(
      axios.get(`${ process.env.REACT_APP_AOE_SECURE_WITH_PATH }/api/feedManagement/autosuggest/location?keyword=${ keyword }${ params ? `&${ queryString.stringify(params) }` : '' }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            const locations = response.data;
            const formattedLocations = locations.map((location) => {
               return {
                  text: location.name,
                  id: parseInt(location.id),
                  locationFullName: location.fullName,
               };
            });
            return formattedLocations;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

// KEEP twin: sjm aoecompat clone (partner auth, same job_feed.taxonomy_category
// table); original routes stay on AOE
export const getTaxonomyCategory = (keyword = '', params) => {
   const baseUrl = isKeepTwinPath()
      ? `${ process.env.REACT_APP_MS_SPONSORED_JOB_META_SECURE }autoSuggest/`
      : AUTO_SUGGEST_BASE_URL;
   return from(
      axios.get(`${ baseUrl }getTaxonomyCategory?keyword=${ keyword }${ params ? `&${ queryString.stringify(params)}` : ''}`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const getTaxonomySubcategory = (keyword = '', params) => {
   return from(
      axios.get(`${ getDomain() }/secure/taxonomySubcategory?keyword=${ keyword }${ params ? `&${ queryString.stringify(params) }` : '' }`),
   ).pipe(
      map((response) => {
         if (response?.data) {
            return response.data;
         } else {
            return [];
         }
      }),
   );
};

// KEEP twin: sjm aoecompat clone (partner auth). sjm only exposes the get-all
// variant, so the keyword match happens client-side; the category list is small
// enough (~40 rows) for that to be equivalent. Original routes stay on AOE.
const getJobCategorySjm = (keyword = '') => {
   return from(
      axios.get(`${ process.env.REACT_APP_MS_SPONSORED_JOB_META_SECURE }autoSuggest/getAllJobCategories`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            const lowerKeyword = `${ keyword }`.toLowerCase();
            return response.data
               .filter((jobCategory) => {
                  return !lowerKeyword || `${ jobCategory.name }`.toLowerCase().includes(lowerKeyword);
               })
               .map((jobCategory) => {
                  return {
                     text: jobCategory.name,
                     id: jobCategory.id,
                  };
               });
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const getJobCategory = (keyword = '', params) => {
   if (isKeepTwinPath()) {
      return getJobCategorySjm(keyword);
   }
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getJobCategory?keyword=${ keyword }${ params ? `&${ queryString.stringify(params)}` : ''}`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            const jobCategories = response.data;
            const formatted_categories = jobCategories.map((jobCategory) => {
               return {
                  text: jobCategory.name,
                  id: jobCategory.id,
               };
            });
            return formatted_categories;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const jobCategoryAutoCompleteInput$ = new BehaviorSubject('');
export const jobCategoryAutoCompleteObservable$ = jobCategoryAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         return getJobCategory(strInput).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

// KEEP twin: sjm aoecompat clone (identical contract); original routes stay on AOE
export const getJobSubcategory = (keyword = '', params) => {
   const baseUrl = isKeepTwinPath()
      ? `${ process.env.REACT_APP_MS_SPONSORED_JOB_META_SECURE }autoSuggest/`
      : AUTO_SUGGEST_BASE_URL;
   return from(
      axios.get(`${ baseUrl }getJobSubcategory?keyword=${ keyword }${ params ? `&${ queryString.stringify(params) }` : '' }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            const jobSubcategories = response.data;
            const formatted_subcategories = jobSubcategories.map((jobSubcategory) => {
               return {
                  text: jobSubcategory.name,
                  id: jobSubcategory.id,
               };
            });
            return formatted_subcategories;
         } else {
            return [];
         }
      }),
      catchError((err) => {console.log(err); return of([]); }),
   );
};
export const jobSubcategoryAutoCompleteInput$ = new BehaviorSubject('');
export const jobSubcategoryAutoCompleteObservable$ = jobSubcategoryAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         return getJobSubcategory(strInput)
            .pipe(
               map((data) => { return data; }),
               catchError((error) => {
                  console.log(error);
                  return of([]);
               }),
            );
      } else {
         return of([]);
      }
   }),
);

export const getChangeLogUserSuggestions = (keyword) => {
   return from(
      axios.get(`${ CHANGE_LOG_BASE_URL }getEmployers?keyword=${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            const users = response.data;
            const formatted_users = users.map((user) => {
               return {
                  text: user.label,
                  description: user.description,
                  id: user.id,
                  userId: user.userId,
               };
            });
            return formatted_users;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const changeLogAutoCompleteInput$ = new BehaviorSubject('');
export const changeLogAutoCompleteObservable$ = changeLogAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         return getChangeLogUserSuggestions(strInput).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getOrganizationsSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }organization?keyword=${ keyword }${ params ? `&${ queryString.stringify(params) }` : ''}`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const organizationAutoCompleteInput$ = new BehaviorSubject('');
export const organizationAutoCompleteObservable$ = organizationAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         const sanatizedStrInput = encodeURIComponent(strInput);
         return getOrganizationsSuggestions(sanatizedStrInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getHiringCompaniesSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }company?keyword=${ keyword }&${ queryString.stringify(params) }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const companyAutoCompleteInput$ = new BehaviorSubject('');
export const companyAutoCompleteObservable$ = companyAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         const sanatizedStrInput = encodeURIComponent(strInput);
         return getHiringCompaniesSuggestions(sanatizedStrInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const companyJobfeedForPartnerAutoCompleteInput$ = new BehaviorSubject('');
export const companyJobfeedForPartnerAutoCompleteObservable$ = companyJobfeedForPartnerAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      const strInput = typeof input === 'object'
         ? input?.keyword
         : input;

      const params = {};
      if (input?.params) {
         for (const [key, value] of Object.entries(input?.params)) {
            if (key) {
               if (Array.isArray(value)) {
                  params[key] = value;
               } else if (value) {
                  params[key] = [value];
               }
            }
         }
      }

      if (strInput.length > 2) {
         return from(getCompanyAutosuggestJF({ keyword: strInput, size: 25, ...params })).pipe(
            map((response) => {
               if (response && response.data) {
                  return response.data?.data?.map((item) => ({ id: item?.id, text: item?.name }));
               } else {
                  return [];
               }
            }),
            catchError((err) => { console.log(err); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);


export const getJobGroupSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }jobGroup?keyword=${ keyword }&${ queryString.stringify(params) }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const jobGroupAutoCompleteInput$ = new BehaviorSubject('');
export const jobGroupAutoCompleteObservable$ = jobGroupAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         const sanatizedStrInput = encodeURIComponent(strInput);
         return getJobGroupSuggestions(sanatizedStrInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getJobSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }job?keyword=${ keyword }&${ queryString.stringify(params) }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const jobAutoCompleteInput$ = new BehaviorSubject('');
export const jobAutoCompleteObservable$ = jobAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         const sanatizedStrInput = encodeURIComponent(strInput);
         return getJobSuggestions(sanatizedStrInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

/*
  Auto Suggest for City
*/

// axios.get( "http://stage.upward.careers/smartcomplete.jsp" + '?type=location&query=' + keyword )
export const getCitySuggestions = (keyword) => {
   return from(
      axios.get(`${ '/smartcomplete.jsp?type=location&query=' }${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return { results: [] };
         }
      }),
      catchError((err) => { console.log(err); return of({ results: [] }); }),
   );
};

export const citySuggestInput$ = new BehaviorSubject('');
export const citySuggestObservable$ = citySuggestInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         return getCitySuggestions(strInput, params).pipe(
            map((data) => {
               if (data.results) {
                  return data.results;
               } else {
                  return [];
               }
            }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

/*
  Auto Suggest for Zip
*/
//     axios.get( "http://stage.upward.careers/includes/zipSearch.jsp" + '?zip=' + keyword )
export const getZipSuggestions = (keyword) => {
   return from(
      axios.get(`${ '/includes/zipSearch.jsp?zip=' }${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return { found: false };
         }
      }),
      catchError((err) => { console.log(err); return of({ found: false }); }),
   );
};

export const zipSuggestInput$ = new BehaviorSubject('');
export const zipSuggestObservable$ = zipSuggestInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   filter((input) => { return input.length > 0; }),
   switchMap((input) => {
      let strInput = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         return getZipSuggestions(strInput).pipe(
            map((data) => {
               return data;
            }),
            catchError((error) => { console.log(error); return of({ found: false }); }),
         );
      } else {
         return of({ found: false });
      }
   }),
);

export const getCompaniesJobfeedSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ BASE_URL }userDBQuery/getCompaniesJF?keyword=${ keyword }&${ queryString.stringify(params) }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const companyJobfeedAutoCompleteInput$ = new BehaviorSubject('');
export const companyJobfeedAutoCompleteObservable$ = companyJobfeedAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         const sanatizedStrInput = encodeURIComponent(strInput);
         return getCompaniesJobfeedSuggestions(sanatizedStrInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getHiringCompaniesJobfeedSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ BASE_URL }autoSuggest/getJobFeedHiringCompany?keyword=${ keyword }&${ queryString.stringify(params) }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const hiringCompanyJobfeedAutoCompleteInput$ = new BehaviorSubject('');
export const hiringCompanyJobfeedAutoCompleteObservable$ = hiringCompanyJobfeedAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         const sanatizedStrInput = encodeURIComponent(strInput);
         return getHiringCompaniesJobfeedSuggestions(sanatizedStrInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const hiringCompanyJobfeedForPartnerAutoCompleteInput$ = new BehaviorSubject('');
export const hiringCompanyJobfeedForPartnerAutoCompleteObservable$ = hiringCompanyJobfeedForPartnerAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      const strInput = typeof input === 'object'
         ? input?.keyword
         : input;

      const params = {};
      if (input?.params) {
         for (const [key, value] of Object.entries(input?.params)) {
            if (key) {
               if (Array.isArray(value)) {
                  params[key] = value;
               } else if (value) {
                  params[key] = [value];
               }
            }
         }
      }

      if (strInput.length > 2) {
         return from(getHiringCompanyAutosuggestJF({ keyword: strInput, ...params })).pipe(
            map((response) => {
               if (response && response.data) {
                  return response.data?.data?.map((item) => ({ id: item?.id, text: item?.name }));
               } else {
                  return [];
               }
            }),
            catchError((err) => { console.log(err); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getOrganizationJobfeedSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ BASE_URL }autoSuggest/getJobFeedOrganization?keyword=${ keyword }&${ queryString.stringify(params) }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const organizationJobfeedForPartnerAutoCompleteInput$ = new BehaviorSubject('');
export const organizationJobfeedForPartnerAutoCompleteObservable$ = organizationJobfeedForPartnerAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      const strInput = typeof input === 'object'
         ? input?.keyword
         : input;

      if (strInput.length > 2) {
         return from(getOrganizationAutosuggestJF({ keyword: strInput, size: 25 })).pipe(
            map((response) => {
               if (response && response.data) {
                  return response.data?.data?.map((item) => ({ id: item?.id, text: item?.name }));
               } else {
                  return [];
               }
            }),
            catchError((err) => { console.log(err); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const organizationJobfeedAutoCompleteInput$ = new BehaviorSubject('');
export const organizationJobfeedAutoCompleteObservable$ = organizationJobfeedAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         const sanatizedStrInput = encodeURIComponent(strInput);
         return getOrganizationJobfeedSuggestions(sanatizedStrInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getJobGroupJobfeedSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ BASE_URL }autoSuggest/getJobFeedJobGroup?keyword=${ keyword }&${ queryString.stringify(params) }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const jobGroupJobfeedForPartnerAutoCompleteInput$ = new BehaviorSubject('');
export const jobGroupJobfeedForPartnerAutoCompleteObservable$ = jobGroupJobfeedForPartnerAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      const strInput = typeof input === 'object'
         ? input?.keyword
         : input;

      const params = {};
      if (input?.params) {
         for (const [key, value] of Object.entries(input?.params)) {
            if (key) {
               if (Array.isArray(value)) {
                  params[key] = value;
               } else if (value) {
                  params[key] = [value];
               }
            }
         }
      }
      if (strInput.length > 2) {
         return from(getJobGroupAutosuggestJF({ keyword: strInput, ...params })).pipe(
            map((response) => {
               if (response && response.data) {
                  return response.data?.data?.map((item) => ({ id: item?.id, text: item?.name }));
               } else {
                  return [];
               }
            }),
            catchError((err) => { console.log(err); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const jobGroupJobfeedAutoCompleteInput$ = new BehaviorSubject('');
export const jobGroupJobfeedAutoCompleteObservable$ = jobGroupJobfeedAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         const sanatizedStrInput = encodeURIComponent(strInput);
         return getJobGroupJobfeedSuggestions(sanatizedStrInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getAudienceNameSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ BASE_URL }/audienceBuilder/getAudienceNames?keyword=${ keyword }&${ queryString.stringify(params) }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const audienceNameAutoCompleteInput$ = new BehaviorSubject('');
export const audienceNameAutoCompleteObservable$ = audienceNameAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         return getAudienceNameSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getAccountSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ BASE_URL }myAccounts/getAccounts?keyword=${ keyword }&${ queryString.stringify(params) }`),
      // axios.get(BASE_URL + 'autoSuggest/getAudienceNames?keyword=' + keyword + "&" + queryString.stringify(params))
   ).pipe(
      map((response) => {
         if (response && response.data) {
            const accounts = response.data;
            const formattedData = accounts.map((account) => {
               return {
                  text: account.label,
                  description: account.description,
                  id: account.id,
                  userId: account.userId,
               };
            });
            return formattedData;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const accountAutoCompleteInput$ = new BehaviorSubject('');
export const accountAutoCompleteObservable$ = accountAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         const sanatizedStrInput = encodeURIComponent(strInput);
         return getAccountSuggestions(sanatizedStrInput).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getjobTitleFromAudiencePivotSuggestions = (keyword) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getJobTitleFromAudiencePivot?keyword=${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const jobTitleFromAudiencePivotAutoCompleteInput$ = new BehaviorSubject('');
export const jobTitleFromAudiencePivotAutoCompleteObservable$ = jobTitleFromAudiencePivotAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         return getjobTitleFromAudiencePivotSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getCompanyTitleFromAudiencePivotSuggestions = (keyword) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getCompanyTitleFromAudiencePivot?keyword=${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const companyTitleFromAudiencePivotAutoCompleteInput$ = new BehaviorSubject('');
export const companyTitleFromAudiencePivotAutoCompleteObservable$ = companyTitleFromAudiencePivotAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         const sanatizedStrInput = encodeURIComponent(strInput);
         return getCompanyTitleFromAudiencePivotSuggestions(sanatizedStrInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getSiteSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getSite?keyword=${ keyword }&${ queryString.stringify(params) }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const siteAutoCompleteInput$ = new BehaviorSubject('');
export const siteAutoCompleteObservable$ = siteAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         return getSiteSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

// KEEP twin: sjm traffic-rule filter (partner auth) — substring-matches on the
// rule name, same {id,name} fields the suggestion list renders. Original routes
// stay on the AOE getRules autosuggest.
export const getAdvertiserRules = (keyword, params) => {
   if (isKeepTwinPath()) {
      return from(
         axios.post(`${ process.env.REACT_APP_MS_SPONSORED_JOB_META_SECURE }traffic-rule/advertiser-rule/filter`,
            { rule: keyword, pageNum: 1, pageSize: 25, order: 'desc' }),
      ).pipe(
         map((response) => {
            if (response && response.data && response.data.data) {
               return response.data.data;
            } else {
               return [];
            }
         }),
         catchError((err) => { console.log(err); return of([]); }),
      );
   }
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getRules?keyword=${ keyword }&type=advertiser`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const advertiserRulesAutoCompleteInput$ = new BehaviorSubject('');
export const advertiserRulesAutoCompleteObservable$ = advertiserRulesAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         return getAdvertiserRules(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const bankIdAutoCompleteInput$ = new BehaviorSubject('');
export const bankIdAutoCompleteObservable$ = bankIdAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length >= 1) {
         return getBankIdSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getBankIdSuggestions = (bankId, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getBankId?bankId=${ bankId}`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const siteIdAutoCompleteInput$ = new BehaviorSubject('');
export const siteIdAutoCompleteObservable$ = siteIdAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length >= 1) {
         return getSiteIdSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

// KEEP twin: sjm traffic-rule filter — see getAdvertiserRules above
export const getPublisherRules = (keyword, params) => {
   if (isKeepTwinPath()) {
      return from(
         axios.post(`${ process.env.REACT_APP_MS_SPONSORED_JOB_META_SECURE }traffic-rule/publisher-rule/filter`,
            { rule: keyword, pageNum: 1, pageSize: 25, order: 'desc' }),
      ).pipe(
         map((response) => {
            if (response && response.data && response.data.data) {
               return response.data.data;
            } else {
               return [];
            }
         }),
         catchError((err) => { console.log(err); return of([]); }),
      );
   }
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getRules?keyword=${ keyword }&type=publisher`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const publisherRulesAutoCompleteInput$ = new BehaviorSubject('');
export const publisherRulesAutoCompleteObservable$ = publisherRulesAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         return getPublisherRules(strInput, params).pipe(
            map((data) => {
               return data;
            }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getSiteIdSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getSite?keyword=${ keyword}`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const CidAutoCompleteInput$ = new BehaviorSubject('');
export const CidAutoCompleteObservable$ = CidAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length >= 1) {
         return getCidSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getPublishers = (keyword, params) => {
   const sanatizedKeyword = encodeURIComponent(keyword);
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getPublishers?keyword=${ sanatizedKeyword }&${ queryString.stringify(params) }`),
   ).pipe(
      map(({data = []}) => (data.map((suggestion) => ({...suggestion, text: suggestion?.name})))),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const publishersAutoCompleteInput$ = new BehaviorSubject('');
export const publishersAutoCompleteObservable$ = publishersAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         const sanatizedStrInput = encodeURIComponent(strInput);
         return getPublishers(sanatizedStrInput, params).pipe(
            map((data) => {
               return data;
            }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

// Legacy compatibility - publishers autosuggest for older Redux-based components
export const publishersForPartnerAutoCompleteInput$ = new BehaviorSubject('');
export const publishersForPartnerAutoCompleteObservable$ = publishersForPartnerAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         return from(getPublisherAutosuggestJF({ keyword: strInput })).pipe(
            map((response) => {
               if (response && response.data) {
                  return response.data?.data?.map((item) => ({ id: item?.id, text: item?.name }));
               } else {
                  return [];
               }
            }),
            catchError((err) => { console.log(err); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getCidSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getContractName?keyword=${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const ProviderAutoCompleteInput$ = new BehaviorSubject('');
export const ProviderAutoCompleteObservable$ = ProviderAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length >= 1) {
         return getProviderSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getProviderSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getProvider?keyword=${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const CarrierAutoCompleteInput$ = new BehaviorSubject('');
export const CarrierAutoCompleteObservable$ = CarrierAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length >= 1) {
         return getCarrierSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getCarrierSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getCarrier?keyword=${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const NumOfPhoneNumbersAutoCompleteInput$ = new BehaviorSubject('');
export const NumOfPhoneNumbersAutoCompleteObservable$ = NumOfPhoneNumbersAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length >= 1) {
         return getNumberOfPhoneNumbersSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getNumberOfPhoneNumbersSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getNumberOfPhoneNumbers?keyword=${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const RefIdAutoCompleteInput$ = new BehaviorSubject('');
export const RefIdAutoCompleteObservable$ = RefIdAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length >= 1) {
         return getRefIdSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getRefIdSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getRefIds?keyword=${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const PhoneNumberAutoCompleteInput$ = new BehaviorSubject('');
export const PhoneNumberAutoCompleteObservable$ = PhoneNumberAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length >= 1) {
         return getPhoneNumberSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getPhoneNumberSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getPhoneNumbers?keyword=${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const VolumeNumberAutoCompleteInput$ = new BehaviorSubject('');
export const VolumeNumberAutoCompleteObservable$ = VolumeNumberAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length >= 1) {
         return getVolumeNumberSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getVolumeNumberSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getVolumeNumbers?keyword=${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const MercuryCampaignAutoCompleteInput$ = new BehaviorSubject('');
export const MercuryCampaignAutoCompleteObservable$ = MercuryCampaignAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length >= 1) {
         return getMercuryCampaignIdSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getContracts = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getContracts?keyword=${ keyword }&${ queryString.stringify(params) }`),
   ).pipe(
      map(({ data = [] }) => (data.map((suggestion) => {
         const name = `(${ suggestion?.id }) ${ suggestion?.name }`;
         return ({
            ...suggestion,
            name,
            text: `(${ suggestion?.id }) ${ suggestion?.name }`,
         });
      }))),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const contractsAutoCompleteInput$ = new BehaviorSubject('');
export const contractsAutoCompleteObservable$ = contractsAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length > 2) {
         return getContracts(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

// Legacy compatibility - contract autosuggest for older Redux-based components
export const contractForPartnerAutoCompleteInput$ = new BehaviorSubject('');
export const contractForPartnerAutoCompleteObservable$ = contractForPartnerAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
      } else {
         strInput = input;
      }

      if (strInput.length > 2) {
         return from(getContractSASuggestions({ keyword: strInput })).pipe(
            map((response) => {
               if (response && response.data) {
                  return response.data?.data?.map((item) => ({ id: item?.id, text: item?.name }));
               } else {
                  return [];
               }
            }),
            catchError((err) => { console.log(err); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const SmsProviderAutoCompleteInput$ = new BehaviorSubject('');
export const SmsProviderAutoCompleteObservable$ = SmsProviderAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length >= 1) {
         return getSmsProviderSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getSmsProviderSuggestions = (keyword,params) => {
   return from(
      axios.get(`${ BASE_URL }smsloadbalance/autosuggest/smsProvider?keyword=${ keyword }`),
   ).pipe(
      first(),
      map((res) => (res && res.data ? res.data : {})),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const getMercuryCampaignIdSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getMercuryCampaignName?keyword=${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const ActionAutoCompleteInput$ = new BehaviorSubject('');
export const ActionAutoCompleteObservable$ = ActionAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length >= 1) {
         return getActionSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getActionSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getAction?keyword=${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const ActiveAutoCompleteInput$ = new BehaviorSubject('');
export const ActiveAutoCompleteObservable$ = ActiveAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length >= 1) {
         return getActiveSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getActiveSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ AUTO_SUGGEST_BASE_URL }getActive?keyword=${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};

export const SmsPhoneBankAutoCompleteInput$ = new BehaviorSubject('');
export const SmsPhoneBankAutoCompleteObservable$ = SmsPhoneBankAutoCompleteInput$.pipe(
   debounceTime(AUTO_SUGGEST_DEBOUNCE_TIME),
   distinctUntilChanged(),
   switchMap((input) => {
      let strInput = '';
      let params = '';
      if (typeof input === 'object') {
         strInput = input.keyword;
         params = input.params;
      } else {
         strInput = input;
      }
      if (strInput.length >= 1) {
         return getSmsPhoneBankSuggestions(strInput, params).pipe(
            map((data) => { return data; }),
            catchError((error) => { console.log(error); return of([]); }),
         );
      } else {
         return of([]);
      }
   }),
);

export const getSmsPhoneBankSuggestions = (keyword, params) => {
   return from(
      axios.get(`${ BASE_URL }smsTesting/autosuggest/smsPhoneBank?keyword=${ keyword }`),
   ).pipe(
      map((response) => {
         if (response && response.data) {
            return response.data;
         } else {
            return [];
         }
      }),
      catchError((err) => { console.log(err); return of([]); }),
   );
};
